<!-- "Save online" / "Load online" (#cloudPickModal, z-56): which service to use for a profile. One row per service
     Hideo can use: its logo, name and state (connected as…, already used for this profile, not connected: sign-in
     follows, coming soon: disabled). A tap closes it, connects the service when needed, then saves (driveSave)
     or lists the files to load (DriveFilesDialog.vue). Own frame (a p-5 card, like the legacy one created on the fly),
     registered in the dialog stack like AppDialog; closes with ×, a click outside or Esc (its own step in src/features/keyboard.ts).
     Opened with openCloudPick(action, profileId) (Profiles rows, the sync indicator) or the cloud store. -->
<script setup lang="ts">
import CardDialog from '../CardDialog.vue'
import { computed } from 'vue'
import { useSyncStore } from '../../stores/sync'
import { useI18n } from '../../composables/useI18n'
import { useDataStore } from '../../stores/data'
import { useUiStore } from '../../stores/ui'
import { useCloudStore } from '../../stores/cloud'
import { cloudPickState } from '../../features/cloud'
import { CS, cloudOn } from '../../features/cloud/common'
import { CLOUDS, CLOUD_ORDER } from '../../features/cloud/services'

const props = defineProps<{ action: 'save' | 'load'; profileId: string }>()
const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()
const store = useDataStore()
const ui = useUiStore()
const cloud = useCloudStore()

const save = computed(() => props.action === 'save')
const name = computed(() => store.profiles.list.find(x => x.id === props.profileId)?.name || '')
const rows = computed(() => {
  void ui.tick; void useSyncStore().ver   // the links of the services change with the save state
  return CLOUD_ORDER.map(id => {
    const P = CLOUDS[id], on = cloudOn(id), linked = on && !!CS(id).links[props.profileId]
    const c = { id, name: P.name, icon: P.icon, configured: P.configured(), on, email: CS(id).email || '' }
    return { ...c, linked, state: cloudPickState(c, linked) }
  })
})

</script>

<template>
  <CardDialog id="cloudPickModal" name="cloudPick" z="z-[56]" dim="bg-black/70" width="max-w-sm" panel-class="p-5 max-h-[90dvh] overflow-y-auto" outside @close="emit('close')">
        <div class="flex items-center justify-between"><h3 class="text-lg font-black text-gray-100"><i :class="['fas', save ? 'fa-cloud-arrow-up' : 'fa-cloud-arrow-down', 'text-brand mr-2']"></i>{{ save ? t('Save online') : t('Load online') }}</h3>
          <button type="button" :title="t('Close')" :aria-label="t('Close')" class="text-gray-400 hover:text-gray-100" @click="emit('close')"><i class="fas fa-xmark text-lg"></i></button></div>
        <p class="text-[11px] text-gray-500 mt-1">{{ save ? t('Where do you want to save the profile "{name}"?', { name }) : t('Where do you want to load the profile "{name}" from?', { name }) }}</p>
        <div class="mt-4 space-y-2">
          <button v-for="c in rows" :key="c.id" type="button" :disabled="!c.configured"
            :class="['w-full text-left flex items-center gap-3 p-3 rounded-2xl border', c.linked ? 'border-brand bg-brand/10' : 'border-gray-800 hover:border-brand/50', 'disabled:opacity-40 disabled:cursor-not-allowed transition']"
            @click="cloud.pickGo(action, profileId, c.id)">
            <span :class="['w-10 h-10 rounded-xl bg-darkbtn flex items-center justify-center text-lg', c.on ? 'text-brand' : 'text-gray-400']"><i :class="c.icon"></i></span>
            <span class="flex-1 min-w-0">
              <span class="block font-bold text-gray-100">{{ c.name }}</span>
              <span :class="['block text-[11px]', c.on ? 'text-green-400' : 'text-gray-500', 'truncate']">{{ c.state }}</span>
            </span>
            <i class="fas fa-chevron-right text-gray-500 text-xs"></i>
          </button>
        </div>
  </CardDialog>
</template>
